import { CommonModule, DatePipe, Location } from '@angular/common';
import { Component, HostListener, OnInit, ElementRef, ViewChild } from '@angular/core';
import lottie from 'lottie-web';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DistributionServiceService } from '../../../services/Distribution-Service/distribution-service.service'
import { NgxPaginationModule } from 'ngx-pagination';
import { LoadingSpinnerComponent } from '../../../components/loading-spinner/loading-spinner.component';
import { ToastAlertService } from '../../../services/toast-alert/toast-alert.service';
import { CustomDatepickerComponent } from "../../../components/custom-datepicker/custom-datepicker.component";
import Swal from 'sweetalert2';
import { TokenServiceService } from '../../../services/Token/token-service.service';
import { of } from 'rxjs';

export interface PackingLineRow {
  id: number;
  companyCenterId: number;
  rowIndex: number;
  isEnabled: number;
  positions: Positions[];
}

export interface Positions {
  id: number;
  rowId: number;
  pIndex: number | null;
  pType: 'QR' | 'QC' | 'NOR';
}

@Component({
  selector: 'app-dch-packing-line',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxPaginationModule, LoadingSpinnerComponent, CustomDatepickerComponent],
  templateUrl: './dch-packing-line.component.html',
  styleUrl: './dch-packing-line.component.css'
})
export class DchPackingLineComponent {

  isLoading: boolean = false;

  regCode!: string;
  centerName!: string;
  centerId!: number;

  rows: PackingLineRow[] = [];
  positions: Positions[] = [];
  currentRow!: PackingLineRow;
  newPos!: number;

  total!: number;

  showCreateRowModal: boolean = false;
  showCreatePositionModal: boolean = false;
  showDeletePositionModal: boolean = false;
  openPositionDeleteErrorPopUp: boolean = false;
  positionToDelete!: Positions;

  logingRole: string | null = null;
  hasData: boolean = false;
  @ViewChild('dchNoRowsAnim') dchNoRowsAnim!: ElementRef;
  private noRowsAnimInstance: any = null;

  openErrorEnableRow: boolean = false;
  openErrorDisableRow: boolean = false;
  openToggleRowPopUpEnable: boolean = false;
  openToggleRowPopUpDisable: boolean = false;
  removeRowIndex!: number;
  rowToToggle!: PackingLineRow;

  constructor(
    private router: Router,
    private DistributionSrv: DistributionServiceService,
    private location: Location,
    private toastSrv: ToastAlertService,
    private route: ActivatedRoute,
    private tokenSrv: TokenServiceService
  ) {
    this.logingRole = tokenSrv.getUserDetails().role
  }


  ngOnInit(): void {
    let center$;
    this.isLoading = true;

    if (this.logingRole === 'Distribution Centre Head') {
      this.centerId = Number(this.route.snapshot.paramMap.get('id'));
      this.centerName = String(this.route.snapshot.paramMap.get('centerName'));
      this.regCode = String(this.route.snapshot.paramMap.get('regCode'));

      center$ = of(null); // Completes immediately
    } else {
      center$ = this.DistributionSrv.getDCMCenterId();
    }

    center$.subscribe({
      next: (res) => {
        if (res !== null) {
          this.centerId = res;
        }

        this.fetchDCHCenterRows();
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  fetchDcmCenterId() {
    this.isLoading = true;
    this.DistributionSrv.getDCMCenterId().subscribe(

      (res) => {
        this.centerId = res;
        this.isLoading = false;
      }
    )
  }

  fetchDCHCenterRows() {
    this.isLoading = true;
    this.DistributionSrv.getDCHCenterRows(this.centerId).subscribe(
      (res) => {
        this.rows = res.items;
        this.total = res.items.length || 0;
        this.hasData = this.rows.length > 0;
        this.isLoading = false;
        if (!this.hasData) {
          setTimeout(() => this.loadNoRowsAnimation(), 0);
        } else if (this.noRowsAnimInstance) {
          this.noRowsAnimInstance.destroy();
          this.noRowsAnimInstance = null;
        }
      }
    )
  }

  private loadNoRowsAnimation() {
    try {
      if (this.noRowsAnimInstance) {
        this.noRowsAnimInstance.destroy();
        this.noRowsAnimInstance = null;
      }
      const container = this.dchNoRowsAnim?.nativeElement;
      if (!container) return;
      this.noRowsAnimInstance = lottie.loadAnimation({
        container,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: 'assets/json/NoRowAvailable.json'
      });
    } catch (err) {
      console.error('Failed to load Lottie animation', err);
    }
  }

  get nextRowNo(): number {
    return this.rows.length + 1;
  }

  get nextPosNo(): number {
    return this.rows.length + 1;
  }

  formatRowNo(rowNo: number): string {
    return rowNo < 10 ? '0' + rowNo : `${rowNo}`;
  }

  getOrderedPositions(row: PackingLineRow): Positions[] {
    const qr = row.positions.filter(p => p.pType === 'QR');
    const nor = row.positions
      .filter(p => p.pType === 'NOR')
      .sort((a, b) => (a.pIndex ?? 0) - (b.pIndex ?? 0));
    const qc = row.positions.filter(p => p.pType === 'QC');
    return [...qr, ...nor, ...qc];
  }

  getPositionLabel(pos: Positions): string {
    return pos.pType === 'NOR' ? `PO${pos.pIndex}` : pos.pType;
  }

  getPositionClass(pType: Positions['pType']): string {
    switch (pType) {
      case 'QR':
        return 'bg-[#FFFFF3] text-[#C97E06] border-[#E69D00]';
      case 'NOR':
        return 'bg-[#EEF2FF] text-[#415CFF] border-[#415CFF]';
      case 'QC':
        return 'bg-[#FFF3FA] text-[#FF4174] border-[#FF4174]';
    }
  }

  openCreateRowModal(): void {
    this.currentRow = this.rows[this.rows.length] || [];
    this.showCreateRowModal = true;
  }

  cancelCreateRow(): void {
    this.showCreateRowModal = false;
  }

  confirmCreateRow(): void {
    const currentRowIndex = this.rows.length > 0 ? this.rows.length : 0
    this.createDCHCenterRow(currentRowIndex + 1);
    this.showCreateRowModal = false;
  }

  createDCHCenterRow(nextRow: number) {
    this.isLoading = true;
    this.DistributionSrv.createDCHCenterRow(this.centerId, nextRow).subscribe(
      (res) => {
        if (res.success) {
          this.toastSrv.success(`Row addition successful.`);
        } else {
          this.toastSrv.error(`Row addition failed.`);
        }

        this.isLoading = false;
        this.fetchDCHCenterRows();
      }
    )
  }

  onToggleRowClick(row: PackingLineRow): void {
    this.rowToToggle = row
    if (this.rowToToggle.isEnabled === 0) {
      this.openToggleRowPopUpEnable = true;
    } else {
      this.openToggleRowPopUpDisable = true;
    }

  }

  confirmToggleRow() {
    this.toggleRow(this.rowToToggle);
    this.openToggleRowPopUpEnable = false;
    this.openToggleRowPopUpEnable = false;
  }

  toggleRow(row: PackingLineRow): void {
    const enableStatus = row.isEnabled === 0 ? 1 : 0;
    this.removeRowIndex = row.rowIndex;
    if (row.positions.length <= 2) {
      this.openErrorEnableRow = true;
      return;
    }
    this.isLoading = true;
    this.DistributionSrv.toggleRow(enableStatus, row.id).subscribe({
      next: (res) => {
        if (row.isEnabled === 0) {
          this.toastSrv.success(`Packing row ${this.rowToToggle.rowIndex} enabled successfully.`);
        } else if (row.isEnabled === 1) {
          this.toastSrv.success(`Packing row ${this.rowToToggle.rowIndex} disabled successfully.`);
        }


        this.fetchDCHCenterRows();
        this.isLoading = false;
        this.openToggleRowPopUpEnable = false;
        this.openToggleRowPopUpDisable = false;
      },
      error: (err) => {
        if (err.status === 401) {
          this.openErrorDisableRow = true;
        } else {
          console.error('Other error:', err);
        }

        this.openToggleRowPopUpEnable = false;
        this.openToggleRowPopUpDisable = false;
        this.isLoading = false;
      }
    });

  }

  openCreatePositionModal(row: PackingLineRow): void {
    this.currentRow = row;
    this.newPos = row.positions.length - 1;
    this.showCreatePositionModal = true;
  }

  cancelCreatePosition(): void {
    this.showCreatePositionModal = false;
  }

  createNextPosNo() {
    this.isLoading = true;
    const nextPos = (this.currentRow.positions.length - 2) + 1;
    this.DistributionSrv.createDCHCenterPos(this.centerId, nextPos, this.currentRow.positions[0].rowId).subscribe(
      (res) => {
        if (res.success) {
          this.toastSrv.success(`Position addition successful.`);
        } else {
          this.toastSrv.error(`Position addition failed.`);
        }

        this.isLoading = false;
        this.showCreatePositionModal = false;
        this.fetchDCHCenterRows();
      }
    )
  }

  cancelCreatePos(): void {
    this.showCreatePositionModal = false;
  }

  openDeletePositionModal(row: PackingLineRow, pos: Positions): void {
    this.currentRow = row;
    this.positionToDelete = pos;
    this.showDeletePositionModal = true;
  }

  cancelDeletePosition(): void {
    this.showDeletePositionModal = false;
  }

  confirmDeletePosition(): void {
    this.isLoading = true;

    this.DistributionSrv.deleteDCHCenterPos(
      this.positionToDelete.id,
      this.positionToDelete.rowId,
      this.positionToDelete.pIndex!
    ).subscribe({
      next: (res) => {
        this.toastSrv.success('Position deletion successful.');

        this.isLoading = false;
        this.fetchDCHCenterRows();
        this.showDeletePositionModal = false;
      },

      error: (err) => {
        if (err.status === 409) {
          this.showDeletePositionModal = false;
          this.openPositionDeleteErrorPopUp = true;
        } else {
          console.error('Other error:', err);
          this.toastSrv.error('An error occurred while deleting position.');
        }
        this.showDeletePositionModal = false;
        this.isLoading = false;
      }
    });
  }

  cancelErrorEnableRow(): void {
    this.openErrorEnableRow = false;
  }

  cancelErrorDisableRow(): void {
    this.openErrorDisableRow = false;
  }

  cancelOpenTogglePopupDisable(): void {
    this.openToggleRowPopUpDisable = false;
  }

  cancelOpenTogglePopupEnable(): void {
    this.openToggleRowPopUpEnable = false;
  }

  cancelPoisitonDeleteErrorPopup(): void {
    this.openPositionDeleteErrorPopUp = false;
  }


}


